"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.NAME_SIMILARITY_WITH_CITY = exports.NAME_SIMILARITY_STRONG = exports.NAME_SIMILARITY_WITH_PHONE = exports.LANGUAGE_LEVEL_FIELD = exports.LANGUAGE_NAME_FIELD = exports.APPLICATION_CONSENT_TEXT = exports.REQUIRED_MESSAGE = exports.DOCUMENT_WRONG_TYPE = exports.DOCUMENT_TOO_LARGE = exports.CV_WRONG_TYPE = exports.CV_TOO_LARGE = exports.DOCUMENT_EXTENSIONS = exports.DOCUMENT_MAX_BYTES = exports.CV_EXTENSIONS = exports.CV_MIME_TYPES = exports.CV_MAX_BYTES = exports.CANDIDATE_ENTITY = exports.APPLICATION_ENTITY = void 0;
exports.parseApplication = parseApplication;
exports.cvProblem = cvProblem;
exports.documentProblem = documentProblem;
exports.standardKnockoutValue = standardKnockoutValue;
exports.evaluateKnockouts = evaluateKnockouts;
exports.passesRule = passesRule;
exports.duplicateVerdict = duplicateVerdict;
exports.trigramSimilarity = trigramSimilarity;
exports.applicationReference = applicationReference;
exports.setFileLimits = setFileLimits;
exports.cvMaxBytes = cvMaxBytes;
exports.documentMaxBytes = documentMaxBytes;
exports.cvTooLarge = cvTooLarge;
exports.documentTooLarge = documentTooLarge;
const enums_1 = require("./enums");
const schemas_1 = require("./schemas");
// S13 — receiving an application (F7.01) and recognising the person (F7.03), moved
// into S13 by the product owner (DECISIONS-LOG S13 Q9). Pure functions, so the rules
// the public form depends on are testable without a browser or a database.
//
// Sources: §L8 (knockouts), §L2 (duplicate tiers), §L26 (field formats and file
// rules), master plan S13 ("forgiving validation", "CV mandatory, PDF/DOC/DOCX, 10MB").
exports.APPLICATION_ENTITY = 'application';
exports.CANDIDATE_ENTITY = 'candidate';
// --- What the form accepts --------------------------------------------------------------
/** §L26 file rules. */
exports.CV_MAX_BYTES = 10 * 1024 * 1024;
exports.CV_MIME_TYPES = {
    'application/pdf': 'pdf',
    'application/msword': 'doc',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
};
exports.CV_EXTENSIONS = ['pdf', 'doc', 'docx'];
/** Custom "file" questions take certificates and documents: PDF, JPG, PNG, 5 MB (§L26). */
exports.DOCUMENT_MAX_BYTES = 5 * 1024 * 1024;
exports.DOCUMENT_EXTENSIONS = ['pdf', 'jpg', 'jpeg', 'png'];
/** §L26: the message states the actual limit, never "Upload failed". */
exports.CV_TOO_LARGE = 'File is too large. Maximum 10 MB.';
exports.CV_WRONG_TYPE = 'Upload your CV as a PDF, DOC or DOCX file.';
exports.DOCUMENT_TOO_LARGE = 'File is too large. Maximum 5 MB.';
exports.DOCUMENT_WRONG_TYPE = 'Upload a PDF, JPG or PNG file.';
exports.REQUIRED_MESSAGE = 'This field is required.';
/** The consent every applicant gives (F7.01 "consent captured", D15). */
exports.APPLICATION_CONSENT_TEXT = 'I agree that my details and CV may be stored and used to consider me for this and similar roles. ' +
    'They are kept for 90 days after my application closes, then removed.';
const MAX_LIST_ITEMS = 30;
const MAX_LIST_ITEM_LENGTH = 60;
const MAX_TEXT = 500;
const MAX_LONG_TEXT = 5000;
const MAX_EXPERIENCE_YEARS = 60;
const MAX_NOTICE_DAYS = 365;
const MAX_SALARY = 999_999_999;
const MIN_AGE = 16;
const MAX_AGE = 70;
const MONTHS_PER_YEAR = 12;
/** Form field names for the language rows, which the page renders as pairs. */
exports.LANGUAGE_NAME_FIELD = 'languages_name';
exports.LANGUAGE_LEVEL_FIELD = 'languages_level';
/**
 * Checks a submission against the job's form definition. Forgiving on purpose
 * (master plan S13): spaces, commas and dashes are stripped before a number or a
 * phone is read, and a message says what to do next (§L26).
 */
function parseApplication(definition, raw, files, today) {
    const errors = {};
    const values = new Map();
    for (const field of definition.fields) {
        const result = readField(field, raw, files, today);
        if (typeof result === 'string') {
            errors[field.key] = result;
        }
        else if (result !== null) {
            values.set(field.key, result.value);
        }
    }
    if (Object.keys(errors).length > 0)
        return { isValid: false, errors };
    const text = (key) => {
        const value = values.get(key);
        return typeof value === 'string' ? value : null;
    };
    const num = (key) => {
        const value = values.get(key);
        return typeof value === 'number' ? value : null;
    };
    const list = (key) => {
        const value = values.get(key);
        return Array.isArray(value) && value.every((item) => typeof item === 'string') ? value : [];
    };
    const languages = values.get('languages');
    const relocate = values.get('willing_to_relocate');
    const years = num('total_experience');
    const standard = {
        fullName: text('full_name') ?? '',
        email: text('email') ?? '',
        phone: text('phone') ?? '',
        totalExperienceMonths: years === null ? null : Math.round(years * MONTHS_PER_YEAR),
        highestEducation: asEducation(text('highest_education')),
        skills: list('skills'),
        expectedSalary: num('expected_salary'),
        noticePeriodDays: num('notice_period'),
        city: text('city'),
        currentEmployer: text('current_employer'),
        currentTitle: text('current_title'),
        isWillingToRelocate: typeof relocate === 'boolean' ? relocate : null,
        linkedinUrl: text('linkedin'),
        languages: Array.isArray(languages) ? languages.filter(isLanguageAnswer) : [],
        certifications: list('certifications'),
        dateOfBirth: text('date_of_birth'),
        gender: text('gender'),
        nationalId: text('national_id'),
    };
    const answers = {};
    for (const [key, value] of values) {
        if (!key.startsWith('q:') || !isAnswerValue(value))
            continue;
        answers[key.slice(2)] = value;
    }
    return { isValid: true, value: { standard, answers } };
}
function readField(field, raw, files, today) {
    if (field.type === 'file')
        return readFile(field, files);
    if (field.key === 'languages')
        return readLanguages(field, raw);
    const values = (raw[field.key] ?? []).map((value) => value.trim()).filter((value) => value !== '');
    if (values.length === 0)
        return field.isRequired ? exports.REQUIRED_MESSAGE : null;
    const first = values[0];
    switch (field.key) {
        case 'full_name':
            return first.length >= 2 && first.length <= 160 ? { value: collapseSpaces(first) } : 'Enter your full name.';
        case 'email':
            return EMAIL_PATTERN.test(first) && first.length <= 254
                ? { value: first.toLowerCase() }
                : 'Enter a valid email address.';
        case 'phone': {
            const compact = first.replace(/[\s\-()]/g, '');
            return BD_MOBILE_PATTERN.test(compact) ? { value: (0, schemas_1.normalisePhone)(compact) } : 'Enter a valid mobile number.';
        }
        case 'total_experience':
            return readNumber(first, { min: 0, max: MAX_EXPERIENCE_YEARS, isWhole: false }, 'Enter your years of experience, for example 3 or 2.5.');
        case 'expected_salary':
            return readNumber(first, { min: 1, max: MAX_SALARY, isWhole: true }, 'Enter a valid amount.');
        case 'notice_period':
            return readNumber(first, { min: 0, max: MAX_NOTICE_DAYS, isWhole: true }, 'Enter your notice period in days, for example 30.');
        case 'skills':
        case 'certifications':
            return { value: splitList(values) };
        case 'linkedin':
            return { value: withScheme(first).slice(0, 300) };
        case 'date_of_birth':
            return readDateOfBirth(first, today);
        case 'national_id': {
            const digits = first.replace(/\s/g, '');
            return /^(\d{10}|\d{13}|\d{17})$/.test(digits) ? { value: digits } : 'NID must be 10, 13, or 17 digits.';
        }
        default:
            return readByType(field, values);
    }
}
function readByType(field, values) {
    const first = values[0];
    const optionValues = field.options.map((option) => option.value);
    switch (field.type) {
        case 'single_select':
            return optionValues.includes(first) ? { value: first } : 'Choose one of the options.';
        case 'multi_select': {
            if (optionValues.length === 0)
                return { value: splitList(values) };
            const chosen = [...new Set(values)];
            return chosen.every((value) => optionValues.includes(value)) ? { value: chosen } : 'Choose from the options given.';
        }
        case 'numeric':
            return readNumber(first, { min: -1e9, max: 1e9, isWhole: false }, 'Enter a number.');
        case 'boolean':
            if (first === 'yes')
                return { value: true };
            if (first === 'no')
                return { value: false };
            return 'Choose yes or no.';
        case 'date':
            return /^\d{4}-\d{2}-\d{2}$/.test(first) ? { value: first } : 'Enter a date in the form 2026-03-26.';
        case 'text':
            return first.length <= MAX_TEXT ? { value: first } : `Keep this under ${MAX_TEXT} characters.`;
        case 'long_text':
            return first.length <= MAX_LONG_TEXT ? { value: first } : `Keep this under ${MAX_LONG_TEXT} characters.`;
        case 'file':
            return null;
    }
}
function readFile(field, files) {
    const file = files[field.key];
    if (!file)
        return field.isRequired ? exports.REQUIRED_MESSAGE : null;
    const problem = field.key === 'cv' ? cvProblem(file) : documentProblem(file);
    return problem ?? { value: file.fileName };
}
/** Type then size, as §L26 words them. */
function cvProblem(file) {
    if (!exports.CV_EXTENSIONS.some((extension) => file.fileName.toLowerCase().endsWith(`.${extension}`)))
        return exports.CV_WRONG_TYPE;
    return file.byteSize > cvLimitBytes ? cvTooLarge() : null;
}
function documentProblem(file) {
    if (!exports.DOCUMENT_EXTENSIONS.some((extension) => file.fileName.toLowerCase().endsWith(`.${extension}`))) {
        return exports.DOCUMENT_WRONG_TYPE;
    }
    return file.byteSize > documentLimitBytes ? documentTooLarge() : null;
}
function readLanguages(field, raw) {
    const names = raw[exports.LANGUAGE_NAME_FIELD] ?? [];
    const levels = raw[exports.LANGUAGE_LEVEL_FIELD] ?? [];
    const rows = [];
    for (const [index, name] of names.entries()) {
        const language = name.trim();
        if (!language)
            continue;
        const level = levels[index] ?? '';
        if (!isProficiency(level))
            return `Choose how well you speak ${language}.`;
        rows.push({ language: language.slice(0, MAX_LIST_ITEM_LENGTH), proficiency: level });
    }
    if (rows.length === 0)
        return field.isRequired ? exports.REQUIRED_MESSAGE : null;
    return { value: rows };
}
function readNumber(text, bounds, message) {
    const cleaned = text.replace(/[,\s৳]/g, '');
    if (!/^-?\d+(\.\d+)?$/.test(cleaned))
        return message;
    const value = Number(cleaned);
    if (bounds.isWhole && !Number.isInteger(value))
        return message;
    return value >= bounds.min && value <= bounds.max ? { value } : message;
}
function readDateOfBirth(text, today) {
    const message = 'Date of birth must give an age between 16 and 70.';
    if (!/^\d{4}-\d{2}-\d{2}$/.test(text))
        return 'Enter a date in the form 1995-03-26.';
    const age = ageOn(text, today);
    return age >= MIN_AGE && age <= MAX_AGE ? { value: text } : message;
}
function ageOn(birth, today) {
    const [by, bm, bd] = birth.split('-').map(Number);
    const [ty, tm, td] = today.split('-').map(Number);
    const hadBirthday = tm > bm || (tm === bm && td >= bd);
    return ty - by - (hadBirthday ? 0 : 1);
}
function splitList(values) {
    const items = values
        .flatMap((value) => value.split(/[,\n;]/))
        .map((item) => collapseSpaces(item).slice(0, MAX_LIST_ITEM_LENGTH))
        .filter((item) => item !== '');
    const seen = new Set();
    return items
        .filter((item) => {
        const key = item.toLowerCase();
        if (seen.has(key))
            return false;
        seen.add(key);
        return true;
    })
        .slice(0, MAX_LIST_ITEMS);
}
function withScheme(url) {
    return /^https?:\/\//i.test(url) ? url : `https://${url}`;
}
function collapseSpaces(text) {
    return text.replace(/\s+/g, ' ').trim();
}
// §L26 email: standard format. Deliberately loose — a strict pattern turns away real
// addresses, and the confirmation that matters is a human reading it later.
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// §L26 phone: 01XXXXXXXXX, +8801XXXXXXXXX or 8801XXXXXXXXX.
const BD_MOBILE_PATTERN = /^(?:\+?880|0)1[3-9]\d{8}$/;
function asEducation(value) {
    return enums_1.EDUCATION_LEVELS.find((level) => level === value) ?? null;
}
function isProficiency(value) {
    return enums_1.LANGUAGE_PROFICIENCIES.includes(value);
}
function isLanguageAnswer(value) {
    return typeof value === 'object' && value !== null && 'language' in value && 'proficiency' in value;
}
function isAnswerValue(value) {
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean')
        return true;
    return Array.isArray(value) && value.every((item) => typeof item === 'string');
}
/** The value a knockout on a standard field compares, in the units the recruiter typed. */
function standardKnockoutValue(field, standard) {
    switch (field) {
        case 'total_experience':
            return standard.totalExperienceMonths === null ? undefined : standard.totalExperienceMonths / MONTHS_PER_YEAR;
        case 'notice_period':
            return standard.noticePeriodDays ?? undefined;
        case 'expected_salary':
            return standard.expectedSalary ?? undefined;
        case 'highest_education':
            return standard.highestEducation ?? undefined;
        case 'willing_to_relocate':
            return standard.isWillingToRelocate ?? undefined;
        case 'city':
            return standard.city ?? undefined;
        default:
            return undefined;
    }
}
/**
 * §L8, evaluated synchronously at submission. Every question is judged on its own;
 * questions sharing a group key with OR logic pass together if any of them passes
 * ("5 years experience OR a professional certification").
 *
 * A question left blank is not evaluated: the form only lets that happen when the
 * question is optional, and rejecting someone for an answer they were allowed to
 * skip would be the system being wrong, which §L8 warns it sometimes is.
 */
function evaluateKnockouts(questions, parsed, today) {
    const results = {};
    for (const question of questions) {
        if (question.knockoutAction === 'none' || !question.knockoutRule) {
            results[question.id] = null;
            continue;
        }
        const value = question.standardField
            ? standardKnockoutValue(question.standardField, parsed.standard)
            : parsed.answers[question.id];
        results[question.id] = value === undefined ? null : passesRule(question.knockoutRule, question.type, value, today);
    }
    const groupPasses = new Map();
    for (const question of questions) {
        if (!question.groupKey || !question.isGroupOrLogic)
            continue;
        const didPass = results[question.id] === true;
        groupPasses.set(question.groupKey, (groupPasses.get(question.groupKey) ?? false) || didPass);
    }
    const isOrGroup = (groupKey) => groupKey !== null && questions.some((question) => question.groupKey === groupKey && question.isGroupOrLogic);
    const failed = questions.filter((question) => {
        if (results[question.id] !== false)
            return false;
        return !(isOrGroup(question.groupKey) && groupPasses.get(question.groupKey ?? '') === true);
    });
    return {
        results,
        isAutoRejected: failed.some((question) => question.knockoutAction === 'auto_reject'),
        isFlagged: failed.some((question) => question.knockoutAction === 'auto_flag'),
        penalty: failed
            .filter((question) => question.knockoutAction === 'score_penalty')
            .reduce((sum, question) => sum + (question.scorePenalty ?? 0), 0),
        failedQuestionIds: failed.map((question) => question.id),
    };
}
/** Whether one answer meets one §L8 condition — the condition is what a candidate must meet to PASS. */
function passesRule(rule, type, value, today) {
    switch (rule.operator) {
        case 'eq':
        case 'neq':
        case 'lt':
        case 'lte':
        case 'gt':
        case 'gte':
            return compareNumbers(rule.operator, value, rule.value);
        case 'between':
            return typeof value === 'number' && rule.min !== undefined && rule.max !== undefined
                ? value >= rule.min && value <= rule.max
                : false;
        case 'is':
            return value === rule.value;
        case 'is_not':
            return value !== rule.value;
        case 'in_list':
            return typeof value === 'string' && (rule.values ?? []).includes(value);
        case 'not_in_list':
            return typeof value === 'string' && !(rule.values ?? []).includes(value);
        case 'contains':
            return containsValue(type, value, rule.value);
        case 'not_contains':
            return !containsValue(type, value, rule.value);
        case 'contains_any':
            return Array.isArray(value) && (rule.values ?? []).some((option) => value.includes(option));
        case 'contains_all':
            return Array.isArray(value) && (rule.values ?? []).every((option) => value.includes(option));
        case 'is_true':
            return value === true;
        case 'is_false':
            return value === false;
        case 'before':
            return typeof value === 'string' && typeof rule.value === 'string' && value < rule.value;
        case 'after':
            return typeof value === 'string' && typeof rule.value === 'string' && value > rule.value;
        case 'within_last_days':
            return typeof value === 'string' && rule.days !== undefined && value <= today && value >= addDays(today, -rule.days);
    }
}
function compareNumbers(operator, value, target) {
    if (typeof value !== 'number' || typeof target !== 'number')
        return false;
    switch (operator) {
        case 'eq':
            return value === target;
        case 'neq':
            return value !== target;
        case 'lt':
            return value < target;
        case 'lte':
            return value <= target;
        case 'gt':
            return value > target;
        case 'gte':
            return value >= target;
    }
}
function containsValue(type, value, target) {
    if (typeof target !== 'string')
        return false;
    if (type === 'multi_select')
        return Array.isArray(value) && value.includes(target);
    return typeof value === 'string' && value.toLowerCase().includes(target.toLowerCase());
}
function addDays(isoDate, days) {
    const date = new Date(`${isoDate}T00:00:00Z`);
    date.setUTCDate(date.getUTCDate() + days);
    return date.toISOString().slice(0, 10);
}
/** §L2's thresholds. */
exports.NAME_SIMILARITY_WITH_PHONE = 0.85;
exports.NAME_SIMILARITY_STRONG = 0.9;
exports.NAME_SIMILARITY_WITH_CITY = 0.85;
/** §L2 match tiers, first match wins. Tiers 1–3 link; 4–6 flag for CAND-06; 7 does nothing. */
function duplicateVerdict(evidence) {
    if (evidence.isNidMatch)
        return { tier: 1, confidence: 100, action: 'link', matchedOn: ['nid'] };
    if (evidence.isEmailMatch)
        return { tier: 2, confidence: 98, action: 'link', matchedOn: ['email'] };
    if (evidence.isPhoneMatch && evidence.nameSimilarity >= exports.NAME_SIMILARITY_WITH_PHONE) {
        return { tier: 3, confidence: 95, action: 'link', matchedOn: ['phone', 'name'] };
    }
    if (evidence.isPhoneMatch)
        return { tier: 4, confidence: 80, action: 'flag', matchedOn: ['phone'] };
    if (evidence.nameSimilarity >= exports.NAME_SIMILARITY_STRONG && (evidence.isSameDateOfBirth || evidence.isSameEmployer)) {
        return {
            tier: 5,
            confidence: 75,
            action: 'flag',
            matchedOn: ['name', evidence.isSameDateOfBirth ? 'date_of_birth' : 'employer'],
        };
    }
    if (evidence.nameSimilarity >= exports.NAME_SIMILARITY_WITH_CITY && evidence.isSameCity) {
        return { tier: 6, confidence: 60, action: 'flag', matchedOn: ['name', 'city'] };
    }
    return { tier: 7, confidence: 0, action: 'none', matchedOn: [] };
}
/**
 * Trigram similarity computed the way Postgres `pg_trgm` computes it (each word padded
 * with two spaces in front and one behind), so the thresholds the database applies and
 * the ones a test asserts are the same number.
 */
function trigramSimilarity(a, b) {
    const left = trigrams(a);
    const right = trigrams(b);
    if (left.size === 0 || right.size === 0)
        return 0;
    let shared = 0;
    for (const gram of left)
        if (right.has(gram))
            shared += 1;
    return shared / (left.size + right.size - shared);
}
function trigrams(text) {
    const grams = new Set();
    for (const word of text.toLowerCase().split(/[^\p{L}\p{N}]+/u).filter((part) => part !== '')) {
        const padded = `  ${word} `;
        const chars = [...padded];
        for (let index = 0; index + 3 <= chars.length; index += 1)
            grams.add(chars.slice(index, index + 3).join(''));
    }
    return grams;
}
/** `APP-2026-00042`. */
function applicationReference(year, sequence) {
    return `APP-${year}-${String(sequence).padStart(5, '0')}`;
}
// --- S31: the limits are ADMIN-15's -------------------------------------------------------------
let cvLimitBytes = exports.CV_MAX_BYTES;
let documentLimitBytes = exports.DOCUMENT_MAX_BYTES;
/** Set from the organisation settings (the API on boot and on every change; the browser at sign-in). */
function setFileLimits(limits) {
    cvLimitBytes = limits.cvMaxBytes;
    documentLimitBytes = limits.documentMaxBytes;
}
function cvMaxBytes() {
    return cvLimitBytes;
}
function documentMaxBytes() {
    return documentLimitBytes;
}
/** §L26: "Maximum 10 MB", with the limit actually in force. */
function cvTooLarge() {
    return `File is too large. Maximum ${Math.round(cvLimitBytes / (1024 * 1024))} MB.`;
}
function documentTooLarge() {
    return `File is too large. Maximum ${Math.round(documentLimitBytes / (1024 * 1024))} MB.`;
}
//# sourceMappingURL=applications.js.map